import React from "react";
import { Link } from "react-router-dom";

const Cookies = () => {
  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Background Pattern */}
      <div 
        className="fixed inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_dealauth-api/artifacts/z6ik3546_Subtle%20Pattern%20On%20Blue.png)',
        }}
      />
      
      {/* Side Fade Overlays */}
      <div className="fixed inset-0 bg-gradient-to-r from-slate-900/60 via-transparent to-slate-900/60 pointer-events-none" />
      
      {/* Header */}
      <header className="relative z-10 border-b border-white/10 bg-slate-900/30 backdrop-blur-md">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-sm text-cyan-400 hover:text-cyan-300">
              ← Back to Deal Room
            </Link>
            <div className="text-sm font-semibold">Jigsaw Conferences Ltd</div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="relative z-10 max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 md:p-12">
          <h1 className="text-3xl font-bold mb-6">Jigsaw Deal Room – Cookie Policy</h1>
          
          <div className="space-y-6 text-slate-300">
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">1. What are cookies?</h2>
              <p>
                Cookies are small text files that are placed on your device when you visit a website. They are widely used to make 
                websites work, or work more efficiently, as well as to provide information to the site owners.
              </p>
              <p className="mt-3">
                This Cookie Policy explains how the Jigsaw Deal Room at <strong>dealroom.jigsawconferences.co.uk</strong> uses cookies.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">2. Our approach to cookies in the Deal Room</h2>
              <p>
                The Deal Room is a private, invitation-only portal used for a specific business purpose. We use a minimal set of cookies 
                that are strictly necessary to:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
                <li>Keep you securely logged in; and</li>
                <li>Remember your basic preferences (for example, that you have seen the cookie banner).</li>
              </ul>
              <p className="mt-3">
                We do not use advertising cookies, and we do not use third-party tracking cookies in the Deal Room.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">3. Cookies we use</h2>
              <p>We currently use the following types of cookies:</p>
              
              <div className="mt-4">
                <h3 className="font-semibold text-white mb-3">Strictly necessary cookies</h3>
                <p className="mb-3">
                  These cookies are essential for the operation of the Deal Room and cannot be switched off in our systems. 
                  They are usually set in response to actions you take, such as logging in or submitting forms.
                </p>
                
                <p className="mb-2 font-semibold text-white">Examples:</p>
                
                <div className="bg-slate-900/30 border border-white/10 rounded-xl p-4 space-y-4">
                  <div>
                    <p className="font-mono text-cyan-300 text-sm mb-1">dealroom_session</p>
                    <p className="text-sm"><strong>Purpose:</strong> Maintains your authenticated session after login so you can access the Deal Room securely.</p>
                    <p className="text-sm"><strong>Type:</strong> First-party, strictly necessary.</p>
                    <p className="text-sm"><strong>Duration:</strong> Up to 60 minutes from your last activity (or as configured).</p>
                  </div>
                  
                  <div className="pt-3 border-t border-white/10">
                    <p className="font-mono text-cyan-300 text-sm mb-1">dealroom_cookie_consent</p>
                    <p className="text-sm"><strong>Purpose:</strong> Stores your choice about the cookie notice so we don’t keep showing it every time you visit.</p>
                    <p className="text-sm"><strong>Type:</strong> First-party, strictly necessary.</p>
                    <p className="text-sm"><strong>Duration:</strong> Up to 12 months.</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-6">
                <h3 className="font-semibold text-white mb-3">No analytics or advertising cookies</h3>
                <p>
                  We do not currently use analytics, performance, or advertising cookies in the Deal Room.
                </p>
                <p className="mt-2">
                  If we decide to introduce analytics cookies in the future, we will update this policy and, where required, 
                  ask for your consent.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">4. Managing cookies</h2>
              <p>
                Because the Deal Room only uses strictly necessary cookies, you cannot disable them via our site without affecting 
                core functionality (such as the ability to log in).
              </p>
              <p className="mt-3">
                You can configure your browser to block or alert you about cookies, but if you disable all cookies, parts of the 
                Deal Room may not function properly or may be inaccessible.
              </p>
              
              <div className="mt-4 bg-blue-900/20 border border-blue-500/30 rounded-xl p-4">
                <p className="text-sm font-semibold text-white mb-2">Instructions for managing cookies are typically available in the “Help” section of your browser or via:</p>
                <ul className="text-sm space-y-1 ml-4">
                  <li>• <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-cyan-300 hover:text-cyan-200 underline">Google Chrome</a></li>
                  <li>• <a href="https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-desktop" target="_blank" rel="noopener noreferrer" className="text-cyan-300 hover:text-cyan-200 underline">Mozilla Firefox</a></li>
                  <li>• <a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-cyan-300 hover:text-cyan-200 underline">Microsoft Edge</a></li>
                  <li>• <a href="https://support.apple.com/en-gb/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-cyan-300 hover:text-cyan-200 underline">Apple Safari</a></li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">5. Changes to this Cookie Policy</h2>
              <p>
                We may update this Cookie Policy from time to time, for example if we introduce additional cookies or change how we use them. 
                Any updates will be posted in the Deal Room with a revised “last updated” date.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">6. Contact</h2>
              <p>If you have any questions about our use of cookies, please contact:</p>
              <p className="mt-2">
                <strong>Email:</strong>{" "}
                <a href="mailto:privacy@jigsawconferences.co.uk" className="text-cyan-400 hover:text-cyan-300 underline">
                  privacy@jigsawconferences.co.uk
                </a>
              </p>
              <p className="mt-1">
                <strong>Postal:</strong> Jigsaw Conferences Ltd, 3rd Floor, 45 Albemarle Street, London, W1S 4JL, England
              </p>
            </section>

            <section className="text-sm text-slate-400 mt-8 pt-6 border-t border-white/10">
              <p><strong>Last updated:</strong> January 2025</p>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 py-6 text-center text-xs text-slate-400">
        <p>© Jigsaw Conferences Ltd. All rights reserved.</p>
        <div className="mt-2 space-x-2">
          <Link to="/legal/terms" className="hover:text-white">Terms</Link>
          <span>|</span>
          <Link to="/legal/privacy" className="hover:text-white">Privacy</Link>
          <span>|</span>
          <Link to="/legal/cookies" className="hover:text-white">Cookies</Link>
        </div>
      </footer>
    </div>
  );
};

export default Cookies;
